import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import PerfectScrollbar from 'react-perfect-scrollbar'

// evertel
import {
    Modal, ModalHeader, ModalBody,
    Row, Col, Button, Badge, Icon, Text, Checkbox, FormInput
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SchemaCategorySearchController } from '@evertel/document'
import { APIDataDocumentSchemaCategory } from '@evertel/types'
import { stringToHslColor } from '@evertel/utils'
import { SchemaBuilderController } from '../../../controller'

interface SchemaCategoriesModalProps {
    visible: boolean
    onClose: () => void
    schemaBuilderController: SchemaBuilderController
}

const SchemaCategoriesModal: React.FC<SchemaCategoriesModalProps> = observer(({
    visible,
    onClose,
    schemaBuilderController
}) => {

    const schemaCategorySearchController = useService(SchemaCategorySearchController, [])

    const [staged, setStaged] = useState<APIDataDocumentSchemaCategory[]>([])
    const [searchValue, setSearchValue] = useState('')

    useEffect(() => {
        if (!visible) return

        setStaged(schemaBuilderController.categories)
        setSearchValue('')
        schemaCategorySearchController.init(schemaBuilderController.departmentId)
        schemaCategorySearchController.search()
    }, [visible])

    const stagedIds = new Set(staged.map(c => c.id as number))

    const onToggleCategory = (category: APIDataDocumentSchemaCategory, checked: boolean) => {
        if (checked) {
            setStaged(prev => [...prev, category])
        } else {
            setStaged(prev => prev.filter(c => c.id !== category.id))
        }
    }

    const onSearchChange = (value: string) => {
        setSearchValue(value)
        schemaCategorySearchController.setSearchTerm(value)
        schemaCategorySearchController.debouncedSearch()
    }

    const onYReachEnd = () => {
        const hasMore = schemaCategorySearchController.categories.length < schemaCategorySearchController.categoriesCount
        if (!hasMore || schemaCategorySearchController.isLoading) return

        schemaCategorySearchController.setPage(schemaCategorySearchController.page + 1)
        schemaCategorySearchController.search()
    }

    const onCancel = () => {
        onClose()
    }

    const onSave = () => {
        schemaBuilderController.setCategories(staged)
        onClose()
    }

    return (
        <Modal
            visible={visible}
            onClose={onCancel}
            size="lg">
            <ModalHeader
                title="Add template to one or more categories"
                subtitle="Select the categories in which you want this template to be included"
            />
            <ModalBody>
                <Text bold>
                    Added to these categories:
                </Text>
                <Row className="flex-wrap mb-3">
                    {(!staged.length) &&
                        <Text
                            tag="p"
                            color="muted">
                            No categories added yet.
                        </Text>
                    }
                    {staged.map((cat) => {
                        if (!cat?.id) return null
                        return (
                            <div
                                key={cat.id}
                                className="me-2 mb-2">
                                <Badge
                                    size={14}
                                    style={{ backgroundColor: stringToHslColor(cat.name as string) }}>
                                    {cat.name}
                                    <Button
                                        className="ms-2 p-0 border-0 bg-transparent"
                                        onClick={() => onToggleCategory(cat, false)}
                                        title="Remove category"
                                        aria-label={`Remove ${cat.name}`}>
                                        <Icon
                                            name="xmark"
                                            color="white"
                                        />
                                    </Button>
                                </Badge>
                            </div>
                        )
                    })}
                </Row>
                <FormInput
                    type="text"
                    name="findCategory"
                    label="Find a category..."
                    placeholder="Find a category..."
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label="Find a category"
                    className="mb-2"
                />
                <PerfectScrollbar
                    options={{ suppressScrollX: true }}
                    onYReachEnd={onYReachEnd}
                    style={{ height: '30vh' }}>
                    {schemaCategorySearchController.categories.map((cat) => {
                        if (!cat?.id) return null
                        const isChecked = stagedIds.has(cat.id)
                        // the checkbox is the (only) keyboard control for the row; the row
                        // itself is just a larger mouse target for it
                        return (
                            <Row
                                key={cat.id}
                                valign="center"
                                onClick={() => onToggleCategory(cat, !isChecked)}
                                className="border rounded p-3 mb-2"
                                style={{ cursor: 'pointer' }}>
                                <Col>
                                    <Text>{cat.name}</Text>
                                </Col>
                                <Col align="right">
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={({ checked }) => onToggleCategory(cat, checked)}
                                            aria-label={`Add to ${cat.name}`}
                                        />
                                    </span>
                                </Col>
                            </Row>
                        )
                    })}
                </PerfectScrollbar>
                <Row className="mt-5" align="right" valign="center">
                    <Button
                        color="link"
                        className="me-4"
                        onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={onSave}>
                        Done
                    </Button>
                </Row>
            </ModalBody>
        </Modal>
    )
})

export { SchemaCategoriesModal }
